import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useBrowserHistory } from '../hooks/useBrowserHistory';
import 'react-loading-skeleton/dist/skeleton.css';

// Función auxiliar para convertir el nombre del cliente a formato de directorio
const getClientDirectory = (clientName) => {
    return '_' + clientName.toLowerCase().replace(/\s+/g, '');
};

// Función para cargar dinámicamente los componentes de medios de pago
const loadPaymentMethod = async (clientDir, methodId) => {
    try {
        const module = await import(`./payment-methods/${clientDir}/${methodId}`);
        return module.default;
    } catch (error) {
        console.error(`Error loading payment method ${methodId}:`, error);
        return null;
    }
};

function PaymentMethodsSkeleton() {
    return (
        <div className="payment-methods-container">
            {/* Header Section */}
            <div className="text-center mb-4">
                <div className="icon-circle icon-circle-lg bg-primary-subtle mx-auto mb-3">
                    <i className="bi bi-credit-card text-primary icon-lg"></i>
                </div>
                <h5 className="mb-2">¿Cómo querés pagar?</h5>
                <p className="text-muted small">
                    Elegí el medio de pago que prefieras
                </p>
            </div>

            {/* Payment Methods Skeleton */}
            <div className="list-group list-group-flush">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="col-12">
                        <div className="card border-0 shadow-sm mb-3">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center">
                                    <Skeleton circle width={48} height={48} className="me-3" />
                                    <div className="flex-grow-1">
                                        <Skeleton width={150} height={20} className="mb-2" />
                                        <Skeleton width={200} height={16} />
                                    </div>
                                    <Skeleton width={20} height={20} />
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PaymentMethodsContainer({ client, onNavigate }) {
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [PaymentMethodComponent, setPaymentMethodComponent] = useState(null);
    const [availableMethods, setAvailableMethods] = useState([]);
    const [loading, setLoading] = useState(true);

    const clientDir = getClientDirectory(client.domain);

    // Configuración de los medios de pago disponibles
    const PAYMENT_METHODS_CONFIG = {
        PagoFacil: {
            id: 'PagoFacil',
            title: 'Pago Fácil',
            description: 'Pagá en efectivo en cualquier sucursal de Pago Fácil',
            icon: 'bi-cash',
            iconBg: 'bg-success-subtle',
            iconColor: 'text-success',
            logo: '/methods/pago-facil.png'
        },
        RapiPago: {
            id: 'RapiPago',
            title: 'RapiPago',
            description: 'Pagá en efectivo en cualquier sucursal de RapiPago',
            icon: 'bi-cash',
            iconBg: 'bg-danger-subtle',
            iconColor: 'text-danger',
            logo: '/methods/rapipago.png'
        },
        CobroExpress: {
            id: 'CobroExpress',
            title: 'Cobro Express',
            description: 'Pagá en efectivo en cualquier sucursal de Cobro Express',
            icon: 'bi-cash',
            iconBg: 'bg-info-subtle',
            iconColor: 'text-info',
            logo: '/methods/cobro-express.png'
        },
        MercadoPago: {
            id: 'MercadoPago',
            title: 'MercadoPago',
            description: 'Pagá con MercadoPago desde tu cuenta o tarjeta',
            icon: 'bi-wallet',
            iconBg: 'bg-primary-subtle',
            iconColor: 'text-primary',
            logo: '/methods/mercadopago.png'
        },
        PagoMisCuentas: {
            id: 'PagoMisCuentas',
            title: 'Pago Mis Cuentas',
            description: 'Pagá desde tu homebanking o aplicación bancaria',
            icon: 'bi-bank',
            iconBg: 'bg-primary-subtle',
            iconColor: 'text-primary',
            logo: '/methods/pago-mis-cuentas.png'
        },
        BankTransfer: {
            id: 'BankTransfer',
            title: 'Transferencia Bancaria',
            description: 'Transferí desde tu homebanking o aplicación bancaria',
            icon: 'bi-bank',
            iconBg: 'bg-primary-subtle',
            iconColor: 'text-primary'
        },
        CashPayment: {
            id: 'CashPayment',
            title: 'Efectivo',
            description: 'Pagá en efectivo en cualquiera de nuestros puntos de cobro',
            icon: 'bi-cash',
            iconBg: 'bg-success-subtle',
            iconColor: 'text-success'
        },
        WhatsApp: {
            id: 'WhatsApp',
            title: 'WhatsApp',
            description: 'Comunicate con nosotros a través de WhatsApp y te guiaremos en el proceso de pago',
            icon: 'bi-whatsapp',
            iconBg: 'bg-success-subtle',
            iconColor: 'text-success'
        },
        CommercePayment: {
            id: 'CommercePayment',
            title: 'Comercio',
            description: 'Pagá en el comercio donde realizaste tu compra',
            icon: 'bi-shop',
            iconBg: 'bg-warning-subtle',
            iconColor: 'text-warning'
        },
        OtherPaymentMethods: {
            id: 'OtherPaymentMethods',
            title: 'Otros Medios de Pago',
            description: 'Contactanos por otros medios de pago o formas de cancelar tus cuotas',
            icon: 'bi-wallet',
            iconBg: 'bg-info-subtle',
            iconColor: 'text-info'
        },
    };

    useEffect(() => {
        // Detectar los medios de pago disponibles para el cliente
        const detectAvailableMethods = async () => {
            setLoading(true);
            const methods = [];
            for (const [key, config] of Object.entries(PAYMENT_METHODS_CONFIG)) {
                try {
                    // Intentamos importar el componente para verificar si existe
                    const exists = await import(`./payment-methods/${clientDir}/${key}.js`)
                        .then(() => true)
                        .catch(() => false);
                    
                    if (exists) {
                        methods.push(config);
                    }
                } catch (error) {
                    console.error(`Error checking payment method ${key}:`, error);
                }
            }
            setAvailableMethods(methods);
            setLoading(false);
        };

        detectAvailableMethods();
    }, [clientDir]);

    useEffect(() => {
        const loadComponent = async () => {
            if (selectedMethod) {
                // Ensure 'payments' state is in history before adding method detail state
                if (!window.history.state || window.history.state.reactState !== 'payments') {
                    window.history.pushState(
                        { reactState: 'payments' },
                        '',
                        window.location.href
                    );
                }
                // Add history entry when navigating to payment method detail
                window.history.pushState(
                    { reactState: `payment-method-${selectedMethod}` },
                    '',
                    window.location.href
                );
                const Component = await loadPaymentMethod(clientDir, selectedMethod);
                setPaymentMethodComponent(() => Component);
            } else {
                setPaymentMethodComponent(null);
            }
        };

        loadComponent();
    }, [selectedMethod, clientDir]);

    const handleBack = () => {
        // Go back in history when clicking back button
        window.history.back();
    };

    // Handle browser back/forward navigation for payment methods
    const handleBrowserStateChange = (newState) => {
        // If state is 'payments', we're back to the list (no method selected)
        if (newState === 'payments') {
            setSelectedMethod(null);
            setPaymentMethodComponent(null);
        } 
        // If state is a payment method, navigate to that method
        else if (newState.startsWith('payment-method-')) {
            const methodId = newState.replace('payment-method-', '');
            setSelectedMethod(methodId);
        }
        // If state is something else (like 'home' or 'loans'), clear selection
        // This will be handled by Client.jsx to change tabs
        else {
            setSelectedMethod(null);
            setPaymentMethodComponent(null);
        }
    };

    // Handle popstate when in detail view to go back to list
    useEffect(() => {
        if (!selectedMethod) return;

        const handlePopState = (event) => {
            // If we're in detail and going back, check if we should go to list
            if (event.state && event.state.reactState === 'payments') {
                setSelectedMethod(null);
                setPaymentMethodComponent(null);
            } else if (!event.state || !event.state.reactState || !event.state.reactState.startsWith('payment-method-')) {
                // If state is not a payment method, go back to list
                setSelectedMethod(null);
                setPaymentMethodComponent(null);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [selectedMethod]);

    // Handle browser back when in payments list - go to home
    useEffect(() => {
        if (selectedMethod) return; // Only handle when in list view

        const handlePopState = (event) => {
            // If we're in payments list and there's no state or state is 'payments', go to home
            if (!event.state || !event.state.reactState || event.state.reactState === 'payments') {
                // Trigger navigation to home via window event
                window.dispatchEvent(new CustomEvent('navigate-to-home'));
                // Push state back to prevent navigation
                window.history.pushState({ reactState: 'payments' }, '', window.location.href);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [selectedMethod]);

    // Only track history when viewing a payment method detail
    // When viewing the list, we handle back manually to go to home
    const currentHistoryState = selectedMethod ? `payment-method-${selectedMethod}` : null;
    useBrowserHistory(currentHistoryState, handleBrowserStateChange, !!selectedMethod);

    if (PaymentMethodComponent) {
        return <PaymentMethodComponent onBack={handleBack} onNavigate={onNavigate} />;
    }

    if (loading) {
        return <PaymentMethodsSkeleton />;
    }

    return (
        <div className="payment-methods-container">
            {/* Header Section */}
            <div className="text-center mb-4">
                <div className="icon-circle icon-circle-lg bg-primary-subtle mx-auto mb-3">
                    <i className="bi bi-credit-card text-primary icon-lg"></i>
                </div>
                <h5 className="mb-2">¿Cómo querés pagar?</h5>
                <p className="text-muted small">
                    Elegí el medio de pago que prefieras
                </p>
            </div>

            {/* Payment Methods List */}
            <div className="list-group list-group-flush">
                {availableMethods.map(method => (
                    <div key={method.id} className="col-12">
                        <div 
                            className="card border-0 shadow-sm mb-3" 
                            role="button"
                            onClick={() => setSelectedMethod(method.id)}
                        >
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center">
                                    {method.logo ? (
                                        <div className="me-3">
                                            <img 
                                                src={method.logo} 
                                                alt={method.title} 
                                                style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                                            />
                                        </div>
                                    ) : (
                                        <div className={`icon-circle ${method.iconBg} me-3`}>
                                            <i className={`bi ${method.icon} ${method.iconColor} icon-md`}></i>
                                        </div>
                                    )}
                                    <div>
                                        <h6 className="mb-1">{method.title}</h6>
                                        <p className="text-muted small mb-0">
                                            {method.description}
                                        </p>
                                    </div>
                                    <i className="bi bi-chevron-right text-muted ms-auto"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Help Section */}
            <div className="text-center mt-4">
                <button className="btn btn-link text-muted btn-sm text-decoration-none">
                    <i className="bi bi-question-circle me-1"></i>
                    ¿Necesitás ayuda?
                </button>
            </div>
        </div>
    );
}

export default PaymentMethodsContainer;